import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { usePressEscape } from 'hooks';
import { updateBookingSelection } from '../../../../actions/schedulerActions';
import { Button, Layout, Modal, Typography } from '@hub-mono/ui';
import { useModalOffset } from '../../../../hooks/useModalOffset';
import styled from 'styled-components';

const REPEAT_MODAL_TYPES = {
    MILESTONE: {
        title: 'Delete milestone',
        description:
            'This is a repeating milestone. Choose if you would like to just delete this milestone, All milestones or all future milestones.',
    },
    PHASE: {
        title: 'Delete phase',
        description:
            'This is a repeating phase. Choose if you would like to just delete this phase, All phases or all future phases.',
    },
    BOOKING: {
        title: 'Delete booking',
        description:
            'This is a repeating booking. Choose if you would like to just delete this booking, All bookings or only future bookings.',
    },
    BOOKING_WITH_DEPENDENCIES: {
        title: 'Delete booking',
        description:
            'This is a repeating booking. Choose if you would like to just delete this booking, All bookings or only future bookings. This booking has multiple parent/child dependencies, after delete, all links will be deleted as well.',
    },
};

const TitleContainer = styled(Layout)`
    min-height: 48px;
`;

const RepeatModal = props => {
    const { type: typeProp, actions, booking } = props;
    const dispatch = useDispatch();
    const offsetClassNames = useModalOffset();

    const [isOpen, setOpen] = useState(true);

    const hideModal = useCallback(() => {
        setOpen(false);
    }, []);
    usePressEscape(hideModal);

    const type =
        typeProp === 'BOOKING' && booking?.childIds?.length > 1 && booking?.parentIds?.length > 1
            ? 'BOOKING_WITH_DEPENDENCIES'
            : typeProp;

    const deleteAll = () => {
        actions.deleteAll();
        hideModal();
        dispatch(updateBookingSelection.request());
    };

    const deleteFuture = () => {
        actions.deleteFuture();
        hideModal();
        dispatch(updateBookingSelection.request());
    };

    const deleteOne = () => {
        actions.deleteOne();
        hideModal();
        dispatch(updateBookingSelection.request());
    };

    const title = (
        <TitleContainer vAlign="center">
            <Typography variant="label" size="xl">
                {REPEAT_MODAL_TYPES[type].title}
            </Typography>
        </TitleContainer>
    );
    const content = <Typography>{REPEAT_MODAL_TYPES[type].description}</Typography>;
    const actionsJSX = (
        <Layout hAlign="flex-end" gap="--Spacing-150">
            <Button onClick={hideModal} variant="text">
                Cancel
            </Button>
            <Button onClick={deleteAll} dataCy="button--delete-all">
                Delete all
            </Button>
            <Button onClick={deleteFuture} dataCy="button--delete-future">
                Delete all future
            </Button>
            <Button onClick={deleteOne} dataCy="button--delete-this">
                Delete this
            </Button>
        </Layout>
    );

    if (!isOpen) {
        return null;
    }

    return (
        <Modal
            topDivider
            bottomDivider={false}
            className={offsetClassNames}
            level="high"
            variant="dialog"
            onClose={hideModal}
            title={title}
            content={content}
            actions={actionsJSX}
            fullWidth="sm"
        />
    );
};

RepeatModal.propTypes = {
    type: PropTypes.oneOf(Object.keys(REPEAT_MODAL_TYPES)).isRequired,
    actions: PropTypes.shape({
        deleteAll: PropTypes.func,
        deleteFuture: PropTypes.func,
        deleteOne: PropTypes.func,
    }),
};

export default RepeatModal;
